// Services
import { apiInstance, gql } from "@/services/httpAdapter";

// Helpers
import { errorLogger } from "@Common/helpers/logger";

// Types
import { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";
import { IReviewData, IReviewDetail } from "@DataPages/types/socialMediaReviews/model";
import { ReviewDialogSource } from "@DataPages/types/socialMediaReviews/utils";

type TIngestSocialMediaReviews = Pick<IReviewData, "_id" | "usersReviews">;

export async function ingestSocialMediaReviews(locationId: string, commissionCycleId: string, payPeriodEndDate: string) {
	const query = gql`
		mutation ingestSocialMediaReviews($locationId: String!, $commissionCycleId: String!, $payPeriodEndDate: String!) {
			ingestSocialMediaReviews(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodEndDate: $payPeriodEndDate) {
				_id
				usersReviews {
					totalReviews
					sitesReviews {
						monthToDate {
							averageRating
							reviewCount
							siteName
						}
						yearToDate {
							averageRating
							reviewCount
							siteName
						}
						allTime {
							averageRating
							reviewCount
							siteName
						}
					}
				}
			}
		}
	`;

	const variables = {
		commissionCycleId,
		locationId,
		payPeriodEndDate,
	};

	try {
		const response: GraphqlQueryResponse<"ingestSocialMediaReviews", TIngestSocialMediaReviews> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.ingestSocialMediaReviews;
	} catch (err) {
		errorLogger(err as Error, { function: "reviewPodiumIngestion", message: "Failed to ingest social media reviews", variables });
		return null;
	}
}

export async function createReviewResponse(args: {
	locationId: string;
	reviewUid: string;
	textResponse: string;
	source: ReviewDialogSource;
	siteName: string;
}) {
	const { locationId, reviewUid, textResponse, source, siteName } = args;
	const query = gql`
		mutation createReviewResponse(
			$locationId: String!
			$reviewUid: String!
			$textResponse: String!
			$source: String!
			$siteName: String!
		) {
			createReviewResponse(
				locationId: $locationId
				reviewUid: $reviewUid
				textResponse: $textResponse
				source: $source
				siteName: $siteName
			)
		}
	`;

	const variables = {
		locationId,
		reviewUid,
		textResponse,
		source,
		siteName,
	};

	try {
		const response: GraphqlQueryResponse<"createReviewResponse", IReviewDetail> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.createReviewResponse ?? null;
	} catch (err) {
		errorLogger(err as Error, { function: "createReviewResponse", message: "Failed to create review response.", variables });
		return null;
	}
}
